import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Share2, Copy, Mail, Facebook, Linkedin, Check } from 'lucide-react';

/**
 * Share button with a small popover of targets:
 *   Copy link | Email | WhatsApp | Facebook | LinkedIn | X/Twitter
 *
 * Props:
 *   url    — absolute URL to share (defaults to window.location.href)
 *   title  — headline used in the share message
 *   text   — optional short description
 *   size   — 'sm' | 'md' — button size preset
 *   variant — 'ghost' | 'outline' — button style preset
 */
export default function ShareButton({
    url,
    title = '',
    text = '',
    size = 'md',
    variant = 'outline',
    className = '',
    testIdPrefix = 'share',
}) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const wrapperRef = useRef(null);
    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    const shareTitle = title || (typeof document !== 'undefined' ? document.title : '');
    const shareText = text || '';

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success('Link copied');
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error('Could not copy link');
        }
    };

    const openTarget = (targetUrl) => {
        window.open(targetUrl, '_blank', 'noopener,noreferrer,width=600,height=650');
        setOpen(false);
    };

    const enc = encodeURIComponent;
    const targets = [
        {
            key: 'copy',
            label: copied ? 'Copied!' : 'Copy link',
            icon: copied ? Check : Copy,
            onClick: handleCopy,
        },
        {
            key: 'email',
            label: 'Email',
            icon: Mail,
            onClick: () => {
                window.location.href = `mailto:?subject=${enc(shareTitle)}&body=${enc(`${shareText ? shareText + '\n\n' : ''}${shareUrl}`)}`;
                setOpen(false);
            },
        },
        {
            key: 'whatsapp',
            label: 'WhatsApp',
            iconRender: () => (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.32.17 11.87c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.67a11.86 11.86 0 0 0 5.7 1.46h.01c6.55 0 11.88-5.32 11.88-11.87 0-3.17-1.23-6.15-3.42-8.44ZM12.06 21.6h-.01a9.7 9.7 0 0 1-4.94-1.35l-.35-.21-3.77.99 1-3.68-.23-.38a9.68 9.68 0 0 1-1.5-5.11c0-5.36 4.36-9.73 9.73-9.73 2.6 0 5.04 1.01 6.88 2.85a9.66 9.66 0 0 1 2.84 6.88c0 5.37-4.36 9.74-9.65 9.74Zm5.61-7.28c-.31-.15-1.82-.9-2.1-1-.28-.1-.49-.15-.7.15-.2.31-.8 1-.98 1.2-.18.2-.36.23-.67.08-.3-.15-1.28-.47-2.44-1.51-.9-.8-1.51-1.8-1.69-2.1-.18-.31-.02-.48.13-.63.13-.13.3-.36.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.7-1.68-.96-2.3-.25-.6-.5-.52-.7-.53h-.6c-.2 0-.53.08-.81.38-.28.31-1.06 1.03-1.06 2.5 0 1.48 1.09 2.9 1.24 3.1.15.2 2.15 3.28 5.2 4.6.73.31 1.3.5 1.74.64.73.23 1.4.2 1.92.13.58-.09 1.82-.74 2.08-1.46.26-.72.26-1.34.18-1.47-.08-.13-.28-.2-.6-.36Z"/>
                </svg>
            ),
            onClick: () => openTarget(`https://wa.me/?text=${enc(`${shareTitle ? shareTitle + ' — ' : ''}${shareUrl}`)}`),
        },
        {
            key: 'facebook',
            label: 'Facebook',
            icon: Facebook,
            onClick: () => openTarget(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`),
        },
        {
            key: 'linkedin',
            label: 'LinkedIn',
            icon: Linkedin,
            onClick: () => openTarget(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`),
        },
        {
            key: 'twitter',
            label: 'X / Twitter',
            iconRender: () => (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2H21l-6.52 7.45L22 22h-6.828l-4.77-6.22L4.8 22H2l6.99-7.98L2 2h6.914l4.312 5.71L18.244 2Zm-1.2 18h1.62L7.03 4H5.295l11.75 16Z"/>
                </svg>
            ),
            onClick: () => openTarget(`https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareTitle)}`),
        },
    ];

    const sizeCls = size === 'sm'
        ? 'text-xs px-3 py-2'
        : 'text-xs px-4 py-3';

    const variantCls = variant === 'ghost'
        ? 'text-[#5B2D8E] hover:bg-[#F2EBE5]'
        : 'border border-[#E5E0D8] bg-white text-[#1A1A2E] hover:bg-[#F9F6F0]';

    const handleMainClick = async (e) => {
        // Native mobile share sheet when available
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
                return;
            } catch {
                // user cancelled or unsupported — fall through to popover
            }
        }
        setOpen((v) => !v);
    };

    return (
        <div ref={wrapperRef} className={`relative inline-block ${className}`}>
            <button
                type="button"
                onClick={handleMainClick}
                className={`${sizeCls} ${variantCls} rounded-sm flex items-center justify-center gap-2 transition-colors`}
                data-testid={`${testIdPrefix}-btn`}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Share2 className="w-4 h-4" />
                Share
            </button>

            {open && (
                <div
                    className="absolute right-0 z-50 mt-2 w-52 bg-white border border-[#E5E0D8] rounded-sm shadow-xl py-1"
                    role="menu"
                    data-testid={`${testIdPrefix}-menu`}
                >
                    {targets.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={t.onClick}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A1A2E] hover:bg-[#F9F6F0] text-left"
                                data-testid={`${testIdPrefix}-${t.key}`}
                                role="menuitem"
                            >
                                <span className="text-[#5B2D8E]">
                                    {t.iconRender ? t.iconRender() : <Icon className="w-4 h-4" />}
                                </span>
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
